(() => {
  const COMPONENT_SELECTOR = 'product-info';
  const MOBILE_MEDIA = window.matchMedia('(max-width: 749px)');

  const getTabs = (wrapper) => Array.from(wrapper.querySelectorAll('[role="tab"]'));
  const getPanels = (wrapper) => Array.from(wrapper.querySelectorAll('.product-horizontal-tabs__panel'));
  const getDetails = (wrapper) =>
    Array.from(wrapper.querySelectorAll('.product-horizontal-tabs__block > details'));

  const updateTabSelection = (wrapper, nextTab) => {
    getTabs(wrapper).forEach((tab) => {
      const isActive = tab === nextTab;
      tab.setAttribute('aria-selected', String(isActive));
      tab.tabIndex = isActive ? 0 : -1;
    });
  };

  const activateTab = (wrapper, nextTab, focusTab = false) => {
    updateTabSelection(wrapper, nextTab);

    getPanels(wrapper).forEach((panel) => {
      panel.hidden = panel.id !== nextTab.getAttribute('aria-controls');
    });

    if (focusTab) nextTab.focus();
  };

  const createMobileIcon = (label) => {
    const normalizedLabel = label.toUpperCase();
    const icon = document.createElement('span');
    icon.className = 'product-horizontal-tabs__mobile-icon';
    icon.setAttribute('aria-hidden', 'true');

    if (normalizedLabel.includes('SIZE')) {
      icon.innerHTML = '<svg viewBox="0 0 24 24" focusable="false"><path d="M3 7.5h18v9H3zM7 7.5v4m4-4v3m4-3v4m4-4v3"/></svg>';
    } else if (normalizedLabel.includes('SHIPPING') || normalizedLabel.includes('RETURN')) {
      icon.innerHTML = '<svg viewBox="0 0 24 24" focusable="false"><path d="M3 6h11v10H3zM14 10h4l3 3v3h-7zM7 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm10 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"/></svg>';
    } else if (normalizedLabel.includes('CARE')) {
      icon.innerHTML = '<svg viewBox="0 0 24 24" focusable="false"><path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10Z"/></svg>';
    } else {
      icon.innerHTML = '<svg viewBox="0 0 24 24" focusable="false"><path d="M9 6h12M9 12h12M9 18h12M3 6h2M3 12h2M3 18h2"/></svg>';
    }

    return icon;
  };

  const setMode = (wrapper) => {
    const isMobile = MOBILE_MEDIA.matches;
    const nextMode = isMobile ? 'mobile' : 'desktop';
    const previousMode = wrapper.dataset.productTabsMode;
    const tabList = wrapper.querySelector('.product-horizontal-tabs__list');
    const tabs = getTabs(wrapper);
    const panels = getPanels(wrapper);
    const detailsElements = getDetails(wrapper);

    wrapper.dataset.productTabsMode = nextMode;

    if (isMobile) {
      tabList.hidden = true;
      tabList.setAttribute('aria-hidden', 'true');

      panels.forEach((panel) => {
        panel.hidden = false;
        panel.removeAttribute('role');
        panel.removeAttribute('aria-labelledby');
        panel.removeAttribute('tabindex');
      });

      detailsElements.forEach((details) => {
        const summary = details.querySelector('summary');
        summary.hidden = false;
        summary.removeAttribute('aria-hidden');

        if (previousMode !== 'mobile') details.open = false;
      });

      return;
    }

    tabList.hidden = false;
    tabList.removeAttribute('aria-hidden');

    panels.forEach((panel) => {
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', panel.dataset.tabId);
      panel.tabIndex = 0;
    });

    detailsElements.forEach((details) => {
      const summary = details.querySelector('summary');
      summary.hidden = true;
      summary.setAttribute('aria-hidden', 'true');
      details.open = true;
    });

    const activeTab =
      tabs.find((tab) => tab.getAttribute('aria-selected') === 'true') || tabs[0];

    if (activeTab) activateTab(wrapper, activeTab);
  };

  const buildTabs = (productInfo) => {
    if (!productInfo || productInfo.dataset.horizontalTabsReady === 'true') return;

    const infoContainer = productInfo.querySelector('[id^="ProductInfo-"]');
    const pageWidth = productInfo.querySelector('product-component > .page-width');
    const productGrid = pageWidth
      ? Array.from(pageWidth.children).find((element) => element.classList.contains('product'))
      : null;

    if (!infoContainer || !productGrid) return;

    const accordions = Array.from(infoContainer.children).filter((element) => {
      return (
        element.matches('.product__accordion.accordion.quick-add-hidden') &&
        element.querySelector('details > summary') &&
        element.querySelector('details > .accordion__content')
      );
    });

    if (!accordions.length) return;

    productInfo.dataset.horizontalTabsReady = 'true';

    const sectionId = productInfo.dataset.section || productInfo.id || 'product';
    const wrapper = document.createElement('div');
    const tabList = document.createElement('div');
    const panelList = document.createElement('div');

    wrapper.className = 'product-horizontal-tabs';
    wrapper.dataset.productHorizontalTabs = sectionId;

    tabList.className = 'product-horizontal-tabs__list';
    tabList.setAttribute('role', 'tablist');
    tabList.setAttribute('aria-label', 'Product information');
    tabList.style.setProperty('--product-tab-count', accordions.length);

    panelList.className = 'product-horizontal-tabs__panels';

    accordions.forEach((accordion, index) => {
      const details = accordion.querySelector('details');
      const summary = details.querySelector('summary');
      const summaryTitle = summary.querySelector('.summary__title');
      const heading = summary.querySelector('.accordion__title');
      const icon = summary.querySelector('.summary__title .svg-wrapper, .summary__title > svg');
      const label = heading?.textContent?.trim() || summary.textContent.trim();
      const tabId = `ProductTab-${sectionId}-${index + 1}`;
      const panelId = `ProductTabPanel-${sectionId}-${index + 1}`;

      const tab = document.createElement('button');
      tab.type = 'button';
      tab.className = 'product-horizontal-tabs__tab';
      tab.id = tabId;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', panelId);
      tab.setAttribute('aria-selected', String(index === 0));
      tab.tabIndex = index === 0 ? 0 : -1;

      if (icon) {
        const iconClone = icon.cloneNode(true);
        iconClone.setAttribute('aria-hidden', 'true');
        tab.append(iconClone);
      }

      const labelElement = document.createElement('span');
      labelElement.className = 'product-horizontal-tabs__label';
      labelElement.textContent = label;
      tab.append(labelElement);

      if (summaryTitle && !summaryTitle.querySelector('.product-horizontal-tabs__mobile-icon')) {
        summaryTitle.prepend(createMobileIcon(label));
      }

      const panel = document.createElement('div');
      panel.className = 'product-horizontal-tabs__panel';
      panel.id = panelId;
      panel.dataset.tabId = tabId;
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tabId);
      panel.tabIndex = 0;
      panel.hidden = index !== 0;

      accordion.classList.add('product-horizontal-tabs__block');
      panel.append(accordion);

      tab.addEventListener('click', () => activateTab(wrapper, tab));

      tab.addEventListener('keydown', (event) => {
        const tabs = getTabs(wrapper);
        const currentIndex = tabs.indexOf(tab);
        let nextIndex = currentIndex;

        if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
        if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = tabs.length - 1;

        if (nextIndex !== currentIndex) {
          event.preventDefault();
          activateTab(wrapper, tabs[nextIndex], true);
        }
      });

      details.addEventListener('toggle', () => {
        if (!MOBILE_MEDIA.matches || !details.open) return;

        getDetails(wrapper).forEach((otherDetails) => {
          if (otherDetails !== details) otherDetails.open = false;
        });

        updateTabSelection(wrapper, tab);
      });

      tabList.append(tab);
      panelList.append(panel);
    });

    wrapper.append(tabList, panelList);
    productGrid.after(wrapper);
    setMode(wrapper);
  };

  const initialize = (root = document) => {
    if (root.matches?.(COMPONENT_SELECTOR)) buildTabs(root);
    root.querySelectorAll?.(COMPONENT_SELECTOR).forEach(buildTabs);
  };

  const refreshModes = () => {
    document.querySelectorAll('.product-horizontal-tabs').forEach(setMode);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));

  document.addEventListener('shopify:block:select', (event) => {
    const panel = event.target.closest?.('.product-horizontal-tabs__panel');
    const wrapper = panel?.closest('.product-horizontal-tabs');

    if (!panel || !wrapper) return;

    if (MOBILE_MEDIA.matches) {
      const details = panel.querySelector('details');
      if (details) details.open = true;
      return;
    }

    const tab = wrapper.querySelector(`[aria-controls="${panel.id}"]`);
    if (tab) activateTab(wrapper, tab);
  });

  if (MOBILE_MEDIA.addEventListener) {
    MOBILE_MEDIA.addEventListener('change', refreshModes);
  } else {
    MOBILE_MEDIA.addListener(refreshModes);
  }
})();
